import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dbAdapter } from '../../db';
import {
  createStoredMedia,
  createStoredMediaUrl,
  createWheelAppearanceTheme,
  defaultWheelAppearance,
  isWheelAppearanceEqual,
  normalizeWheelAppearance,
  normalizeWheelAppearanceTheme,
  normalizeWheelAppearanceThemeName,
  setAppearanceValue,
} from './wheelAppearance';

const createThemeId = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `wheel-theme-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const createMediaUrls = (media) => {
  const urls = {
    overlay: null,
    center: null,
    pointer: null,
    sectors: {},
    sounds: {
      start: null,
      tick: null,
      win: null,
    },
  };
  const createdUrls = [];

  const createUrl = (asset) => {
    if (typeof asset?.url === 'string' && asset.url.trim()) {
      return asset.url.trim();
    }

    if (!asset?.blob || typeof URL.createObjectURL !== 'function') {
      return null;
    }
    const url = URL.createObjectURL(asset.blob);
    createdUrls.push(url);
    return url;
  };

  urls.overlay = createUrl(media.overlay);
  urls.center = createUrl(media.center);
  urls.pointer = createUrl(media.pointer);
  Object.entries(media.sectors || {}).forEach(([id, asset]) => {
    urls.sectors[id] = createUrl(asset);
  });
  Object.entries(media.sounds || {}).forEach(([id, asset]) => {
    urls.sounds[id] = createUrl(asset);
  });

  return { urls, createdUrls };
};

export const useWheelAppearance = ({ initialThemeId, fallbackWheel }) => {
  const initialThemeIdRef = useRef(initialThemeId);
  const fallbackWheelRef = useRef(fallbackWheel);
  const [appearance, setAppearance] = useState(defaultWheelAppearance);
  const [baseAppearance, setBaseAppearance] = useState(defaultWheelAppearance);
  const [defaultAppearance, setDefaultAppearance] = useState(defaultWheelAppearance);
  const [appearanceThemes, setAppearanceThemes] = useState([]);
  const [activeThemeId, setActiveThemeId] = useState('');
  const [storedDraft, setStoredDraft] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({
    overlay: null,
    center: null,
    pointer: null,
    sectors: {},
    sounds: {
      start: null,
      tick: null,
      win: null,
    },
  });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    dbAdapter.migrateWheelAppearanceThemes({
      themeId: initialThemeIdRef.current,
      fallbackWheel: fallbackWheelRef.current,
    })
      .then(({ themes, draft }) => {
        if (!isMounted) {
          return;
        }

        setAppearanceThemes((themes || [])
          .map((theme) => normalizeWheelAppearanceTheme(theme, fallbackWheelRef.current))
          .filter(Boolean));
        setStoredDraft(draft?.value ? {
          themeId: draft.themeId,
          value: normalizeWheelAppearance(draft.value),
        } : null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (isMounted) {
          setIsLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const { urls, createdUrls } = createMediaUrls(appearance.media);
    setMediaUrls(urls);

    return () => {
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [appearance.media]);

  const isDirty = useMemo(
    () => !isWheelAppearanceEqual(appearance, baseAppearance),
    [appearance, baseAppearance],
  );

  useEffect(() => {
    if (!isLoaded || !activeThemeId) {
      return undefined;
    }

    const saveTimeout = setTimeout(() => {
      if (isDirty) {
        dbAdapter.saveWheelAppearanceDraft(activeThemeId, appearance).catch(() => undefined);
      } else {
        dbAdapter.clearWheelAppearanceDraft().catch(() => undefined);
      }
    }, 250);

    return () => clearTimeout(saveTimeout);
  }, [activeThemeId, appearance, isDirty, isLoaded]);

  const selectTheme = useCallback((
    themeId,
    themeAppearance,
    themeDefaultAppearance = themeAppearance,
  ) => {
    const normalizedBaseAppearance = normalizeWheelAppearance(themeAppearance);
    const normalizedDefaultAppearance = normalizeWheelAppearance(themeDefaultAppearance);
    const restoredAppearance = storedDraft?.themeId === themeId
      ? normalizeWheelAppearance(storedDraft.value)
      : normalizedBaseAppearance;

    setActiveThemeId(themeId);
    setBaseAppearance(normalizedBaseAppearance);
    setDefaultAppearance(normalizedDefaultAppearance);
    setAppearance(restoredAppearance);
  }, [storedDraft]);

  const updateValue = useCallback((path, value) => {
    setAppearance((current) => setAppearanceValue(current, path, value));
  }, []);

  const updateSector = useCallback((sectorId, property, value) => {
    setAppearance((current) => ({
      ...current,
      sectorOverrides: {
        ...current.sectorOverrides,
        [sectorId]: {
          ...current.sectorOverrides[sectorId],
          [property]: value,
        },
      },
    }));
  }, []);

  const setMedia = useCallback((slot, file, sectorId) => {
    setAppearance((current) => {
      if (slot === 'sound') {
        return {
          ...current,
          media: {
            ...current.media,
            sounds: {
              ...current.media.sounds,
              [sectorId]: createStoredMedia(file),
            },
          },
        };
      }

      if (slot === 'sector') {
        const sectors = { ...current.media.sectors };
        if (file) {
          sectors[sectorId] = createStoredMedia(file);
        } else {
          delete sectors[sectorId];
        }
        return {
          ...current,
          media: { ...current.media, sectors },
        };
      }

      return {
        ...current,
        media: {
          ...current.media,
          [slot]: createStoredMedia(file),
        },
      };
    });
  }, []);

  const setMediaUrl = useCallback((slot, url, sectorId, fallbackKind = 'image') => {
    setAppearance((current) => {
      const nextAsset = createStoredMediaUrl(url, fallbackKind);

      if (slot === 'sound') {
        return {
          ...current,
          media: {
            ...current.media,
            sounds: {
              ...current.media.sounds,
              [sectorId]: nextAsset,
            },
          },
        };
      }

      if (slot === 'sector') {
        const sectors = { ...current.media.sectors };
        if (nextAsset) {
          sectors[sectorId] = nextAsset;
        } else {
          delete sectors[sectorId];
        }
        return {
          ...current,
          media: { ...current.media, sectors },
        };
      }

      return {
        ...current,
        media: {
          ...current.media,
          [slot]: nextAsset,
        },
      };
    });
  }, []);

  const clearDraft = useCallback(async () => {
    setStoredDraft(null);
    try {
      await dbAdapter.clearWheelAppearanceDraft();
    } catch {
      // Keep the restored state even if browser storage is unavailable.
    }
  }, []);

  const resetAppearance = useCallback(async () => {
    setAppearance(normalizeWheelAppearance(defaultAppearance));
    await clearDraft();
  }, [clearDraft, defaultAppearance]);

  const discardAppearanceChanges = useCallback(async () => {
    setAppearance(normalizeWheelAppearance(baseAppearance));
    await clearDraft();
  }, [baseAppearance, clearDraft]);

  const saveAppearanceTheme = useCallback(async (name, wheel) => {
    const theme = createWheelAppearanceTheme(name, appearance, wheel, createThemeId());
    if (!theme) {
      return null;
    }

    try {
      await dbAdapter.saveWheelAppearanceTheme(theme);
      await dbAdapter.clearWheelAppearanceDraft();
      setAppearanceThemes((current) => [theme, ...current]);
      setStoredDraft(null);
      setBaseAppearance(theme.appearance);
      return theme;
    } catch {
      return null;
    }
  }, [appearance]);

  const updateAppearanceTheme = useCallback(async (
    themeId,
    wheel,
    fallbackName,
    nextAppearance = appearance,
  ) => {
    const existingTheme = appearanceThemes.find((theme) => theme.id === themeId);
    const normalizedFallbackName = normalizeWheelAppearanceThemeName(fallbackName);
    if (!themeId || !wheel || (!existingTheme && !normalizedFallbackName)) {
      return null;
    }

    const normalizedAppearance = normalizeWheelAppearance(nextAppearance);
    const updatedAt = Date.now();
    const theme = {
      ...existingTheme,
      id: themeId,
      name: existingTheme?.name || normalizedFallbackName,
      type: 'custom',
      wheel,
      appearance: normalizedAppearance,
      createdAt: existingTheme?.createdAt || updatedAt,
      updatedAt,
    };

    try {
      await dbAdapter.saveWheelAppearanceTheme(theme);
      setAppearanceThemes((current) => [
        theme,
        ...current.filter((currentTheme) => currentTheme.id !== themeId),
      ]);
      if (activeThemeId === themeId) {
        setAppearance(normalizedAppearance);
        setBaseAppearance(normalizedAppearance);
        setStoredDraft(null);
        await dbAdapter.clearWheelAppearanceDraft();
      }
      return theme;
    } catch {
      return null;
    }
  }, [activeThemeId, appearance, appearanceThemes]);

  const importAppearanceTheme = useCallback(async ({ name, appearance: importedAppearance, wheel }) => {
    const theme = createWheelAppearanceTheme(
      name,
      importedAppearance,
      wheel,
      createThemeId(),
    );
    if (!theme) {
      return null;
    }

    try {
      await dbAdapter.saveWheelAppearanceTheme(theme);
      setAppearanceThemes((current) => [theme, ...current]);
      return theme;
    } catch {
      return null;
    }
  }, []);

  const renameAppearanceTheme = useCallback(async (themeId, name) => {
    const existingTheme = appearanceThemes.find((theme) => theme.id === themeId);
    const normalizedName = normalizeWheelAppearanceThemeName(name);
    if (!existingTheme || !normalizedName) {
      return null;
    }

    const theme = {
      ...existingTheme,
      name: normalizedName,
      updatedAt: Date.now(),
    };

    try {
      await dbAdapter.saveWheelAppearanceTheme(theme);
      setAppearanceThemes((current) => [
        theme,
        ...current.filter((currentTheme) => currentTheme.id !== themeId),
      ]);
      return theme;
    } catch {
      return null;
    }
  }, [appearanceThemes]);

  const removeAppearanceTheme = useCallback(async (themeId) => {
    try {
      await dbAdapter.removeWheelAppearanceTheme(themeId);
      setAppearanceThemes((current) => current.filter((theme) => theme.id !== themeId));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    appearance,
    mediaUrls,
    isLoaded,
    isDirty,
    activeThemeId,
    updateValue,
    updateSector,
    setMedia,
    setMediaUrl,
    resetAppearance,
    discardAppearanceChanges,
    selectTheme,
    appearanceThemes,
    saveAppearanceTheme,
    updateAppearanceTheme,
    importAppearanceTheme,
    renameAppearanceTheme,
    removeAppearanceTheme,
  };
};
